import React, { useContext } from 'react';

import { CartContext } from '@repo/contexts/CartContext';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';

import {
  CartContextProps,
  SearchResultsProps,
} from './BlockBuilderDynamicDealerPricing';

function SearchResults({
  isCompanyOwnedDealer,
  pricingColumnHeader,
  pricingHeader,
  paymentDetailText,
  leftColumnShowPrice,
  leftColumnUseCustomPrice,
  leftColumnCustomPrice,
  leftColumnVariant,
  leftColumnPriceDescriptorText,
  leftColumnPaymentDetailText,
  rightColumnShowPrice,
  rightColumnUseCustomPrice,
  rightColumnCustomPrice,
  rightColumnVariant,
  rightColumnPriceDescriptorText,
  rightColumnPaymentDetailText,
  getInternalLink,
  product,
}: Readonly<SearchResultsProps>) {
  const { formatPrice } = useContext<CartContextProps>(CartContext);

  const getSalePrice = (productVariant: any) => (
    <>
      <span className="line-through">
        {formatPrice(productVariant.store.compareAtPrice)}
      </span>
      <br />
      <span className="text-destructive">
        SALE {formatPrice(productVariant.store.price)}
      </span>
    </>
  );

  const getPrice = (productVariant: any) => {
    if (productVariant?.store?.compareAtPrice && productVariant?.store?.price) {
      return getSalePrice(productVariant);
    }

    if (productVariant?.store?.price) {
      return formatPrice(productVariant.store.price);
    }

    if (
      product?.store?.variants?.[0]?.store?.compareAtPrice &&
      product?.store?.variants?.[0]?.store?.price
    ) {
      return getSalePrice(product.store.variants[0]);
    }

    if (product?.store?.variants?.[0]?.store?.price) {
      return formatPrice(product.store.variants[0]?.store.price);
    }

    return '';
  };

  if (isCompanyOwnedDealer) {
    return (
      <div className="flex flex-col gap-2 items-start self-stretch">
        {pricingColumnHeader && (
          <p className="m-0 xs:text-center self-stretch">
            {pricingColumnHeader}
          </p>
        )}
        <div className="flex flex-col xs:flex-row items-start self-stretch">
          <div className="flex flex-col gap-2 border-border border-b xs:border-b-0 xs:border-r pb-3 xs:pb-0 xs:pr-3 items-center flex-1">
            {leftColumnShowPrice && (
              <div className="flex flex-col items-start w-full max-w-60">
                <p className="m-0 text-foreground-headline text-2xl font-bold leading-[115%]">
                  {leftColumnUseCustomPrice
                    ? `${leftColumnCustomPrice || ''}`
                    : getPrice(leftColumnVariant)}
                </p>
                {leftColumnPriceDescriptorText && (
                  <p className="m-0 text-sm leading-[115%]">
                    {leftColumnPriceDescriptorText}
                  </p>
                )}
              </div>
            )}

            {leftColumnPaymentDetailText?.text?.length > 0 && (
              <div className="flex flex-col items-start w-full max-w-60">
                <div className="w-full m-0 text-sm font-normal text-foreground [&>p]:mb-0">
                  <BlockBuilder
                    blocks={leftColumnPaymentDetailText.text}
                    getInternalLink={getInternalLink}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 pt-3 xs:pt-0 xs:pl-3 items-center flex-1">
            {rightColumnShowPrice && (
              <div className="flex flex-col items-start w-full max-w-60">
                <p className="m-0 text-foreground-headline text-2xl font-bold leading-[115%]">
                  {rightColumnUseCustomPrice
                    ? `${rightColumnCustomPrice || ''}`
                    : getPrice(rightColumnVariant)}
                </p>
                {rightColumnPriceDescriptorText && (
                  <p className="m-0 text-sm leading-[115%]">
                    {rightColumnPriceDescriptorText}
                  </p>
                )}
              </div>
            )}

            {rightColumnPaymentDetailText?.text?.length > 0 && (
              <div className="flex flex-col items-start w-full max-w-60">
                <div className="w-full m-0 text-sm font-normal text-foreground [&>p]:mb-0">
                  <BlockBuilder
                    blocks={rightColumnPaymentDetailText.text}
                    getInternalLink={getInternalLink}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 items-start self-center max-w-xs w-full">
      {pricingHeader && <p className="m-0 self-stretch">{pricingHeader}</p>}
      <div className="flex flex-col gap-2 items-center flex-1">
        {paymentDetailText?.text?.length > 0 && (
          <div className="flex flex-col items-start">
            <div className="w-full m-0 text-sm font-normal text-foreground [&>p]:mb-0">
              <BlockBuilder
                blocks={paymentDetailText.text}
                getInternalLink={getInternalLink}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchResults;
